import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  numberAttribute,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { UiButtonComponent } from '../../atoms/button/button.component';
import { UiMultiselectComponent } from '../../atoms/multiselect/multiselect.component';
import {
  UiSelectComponent,
  UiSelectOption,
} from '../../atoms/select/select.component';
import { UiTagColor, UiTagComponent } from '../../atoms/tag/tag.component';
import { UiDataTableActionsMenuComponent } from '../../organisms/data-table/data-table-actions-menu.component';
import { computeDataTableColumnWidths } from '../../organisms/data-table/data-table.col-widths';
import {
  DATA_TABLE_MULTI_FILTER_SEP,
  DataGridColumn,
  DataTableColumnSizing,
  parseColumnMultiFilter,
  RowAction,
  SortState,
} from '../../organisms/data-table/data-table.types';
import {
  UiListReportAction,
  UiListReportColumn,
  UiListReportTotalValues,
} from './list-report.types';

export type {
  UiListReportAction,
  UiListReportColumn,
  UiListReportFilterType,
  UiListReportTotalValues,
} from './list-report.types';

const DEFAULT_VISIBLE_ROWS = 5;
const BORDER_HEIGHT = 2;
const HEADER_HEIGHT = 40;
const DIVIDER_HEIGHT = 1;
const COLUMN_HEADER_HEIGHT = 32;
const FILTER_ROW_HEIGHT = 48;
const ROW_HEIGHT = 40;
const TOTAL_ROW_HEIGHT = 40;
const MAX_TREE_DEPTH = 5;
const TREE_INDENT = 16;

interface ListReportDisplayRow<T> {
  readonly row: T;
  readonly level: number;
  readonly id: string;
  readonly expandable: boolean;
  readonly expanded: boolean;
}

/**
 * Molécula: listado compacto para paneles de control.
 * Presenta encabezado, filas visibles configurables y acciones por registro.
 */
@Component({
  selector: 'ui-list-report',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UiButtonComponent,
    UiMultiselectComponent,
    UiSelectComponent,
    UiTagComponent,
    UiDataTableActionsMenuComponent,
  ],
  templateUrl: './list-report.component.html',
  styleUrl: './list-report.component.scss',
  host: { class: 'ui-list-report-host' },
})
export class UiListReportComponent<T = unknown>
  implements AfterViewInit, OnDestroy
{
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly title = input.required<string>();
  readonly showTitle = input(true);
  readonly columns = input.required<readonly UiListReportColumn<T>[]>();
  readonly rows = input<readonly T[]>([]);
  readonly rowKey = input('id');
  readonly actions = input<readonly UiListReportAction<T>[]>([]);
  readonly columnSizing = input<DataTableColumnSizing>('auto');
  readonly moreLabel = input('Ver más');
  readonly showMore = input(true);
  readonly visibleRowCount = input(DEFAULT_VISIBLE_ROWS, {
    transform: numberAttribute,
  });
  readonly showTotals = input(false);
  readonly totalLabel = input('Total');
  readonly totalValues = input<UiListReportTotalValues>({});
  /** Muestra un mensaje dentro de la tabla cuando no existen registros. */
  readonly showEmptyState = input(false);
  readonly emptyText = input('Sin resultados');
  readonly showColumnFilters = input(false);
  /** Muestra la barra de desplazamiento horizontal cuando las columnas exceden el ancho. */
  readonly showHorizontalScrollbar = input(false);
  /** Primeras columnas que permanecen visibles al desplazar en horizontal. */
  readonly stickyColumnCount = input(0, { transform: numberAttribute });
  /** Mantiene fija la última columna al desplazar en horizontal. */
  readonly stickyEndColumn = input(false);
  /** Marca filas con el fondo de los títulos y texto en semibold. */
  readonly emphasizeRow = input<((row: T) => boolean) | undefined>(undefined);
  /** Habilita el clic en la fila. Por defecto las filas no son interactivas. */
  readonly clickableRows = input(false);

  readonly moreClick = output<MouseEvent>();
  readonly rowClick = output<T>();
  readonly actionSelect = output<{ action: UiListReportAction<T>; row: T }>();
  readonly columnFiltersChange = output<Record<string, string>>();
  readonly sortChange = output<SortState>();

  protected readonly menu = signal<{
    row: T;
    rowIndex: number;
    top: number;
    left: number;
    openAbove: boolean;
    items: readonly RowAction[];
  } | null>(null);
  protected readonly columnFilters = signal<Record<string, string>>({});
  protected readonly openFilterKey = signal<string | null>(null);
  protected readonly sortState = signal<SortState | null>(null);
  protected readonly expandedIds = signal<ReadonlySet<string>>(new Set());
  protected readonly sortedRows = computed(() => {
    const rows = this.rows();
    const state = this.sortState();
    const column = this.columns().find((item) => item.key === state?.sortBy);
    if (!state || !column || !this.isColumnSortable(column)) return rows;
    const direction = state.order === 'asc' ? 1 : -1;
    return [...rows].sort(
      (left, right) =>
        direction *
        compareSortValues(
          this.sortValue(column, left),
          this.sortValue(column, right),
        ),
    );
  });
  protected readonly normalizedVisibleRowCount = computed(() =>
    Math.max(1, Math.floor(this.visibleRowCount() || DEFAULT_VISIBLE_ROWS)),
  );
  protected readonly normalizedStickyCount = computed(() =>
    Math.max(
      0,
      Math.min(
        Math.floor(this.stickyColumnCount() || 0),
        this.columns().length,
      ),
    ),
  );
  protected readonly hasExpandableRows = computed(() =>
    this.branchHasChildren(this.rows(), 0),
  );
  protected readonly displayRows = computed(() => {
    const expanded = this.expandedIds();
    const result: ListReportDisplayRow<T>[] = [];
    const visit = (rows: readonly T[], level: number, path: string): void => {
      rows.forEach((row, index) => {
        const id = `${path}${this.rowId(row, index)}`;
        const expandable =
          level < MAX_TREE_DEPTH - 1 && this.rowChildren(row).length > 0;
        const isExpanded = expandable && expanded.has(id);
        result.push({ row, level, id, expandable, expanded: isExpanded });
        if (isExpanded) visit(this.rowChildren(row), level + 1, `${id}/`);
      });
    };
    visit(this.sortedRows(), 0, '');
    return result;
  });
  protected readonly hasVerticalOverflow = computed(
    () => this.displayRows().length > this.normalizedVisibleRowCount(),
  );
  protected readonly fillerRows = computed(() =>
    Array.from({
      length: Math.max(
        0,
        this.normalizedVisibleRowCount() - this.displayRows().length,
      ),
    }),
  );
  protected readonly stickyLeftOffsets = computed(() => {
    const offsets: Record<string, number> = {};
    let left = 0;
    const widths = this.colWidths();
    const columns = this.columns().slice(0, this.normalizedStickyCount());
    for (const column of columns) {
      offsets[column.key] = left;
      left +=
        Number.parseFloat(widths[column.key] ?? '') || column.width || 0;
    }
    return offsets;
  });
  protected readonly viewportHeight = computed(
    () =>
      COLUMN_HEADER_HEIGHT +
      (this.showColumnFilters() ? FILTER_ROW_HEIGHT : 0) +
      this.normalizedVisibleRowCount() * ROW_HEIGHT +
      (this.showTotals() ? TOTAL_ROW_HEIGHT : 0),
  );
  protected readonly reportHeight = computed(
    () =>
      BORDER_HEIGHT +
      (this.showTitle() ? HEADER_HEIGHT + DIVIDER_HEIGHT : 0) +
      this.viewportHeight(),
  );
  protected readonly colWidths = signal<Record<string, string>>({});
  protected readonly tableWidth = signal('100%');
  protected readonly columnsReady = signal(false);
  private resizeObserver: ResizeObserver | null = null;
  private readonly scrollHandler = () => this.closeActions();

  constructor() {
    document.addEventListener('scroll', this.scrollHandler, {
      capture: true,
      passive: true,
    });
    effect(() => {
      this.columns();
      this.rows();
      this.actions();
      this.columnSizing();
      this.visibleRowCount();
      this.showTitle();
      this.showColumnFilters();
      this.showTotals();
      this.totalValues();
      setTimeout(() => this.calculateColumnWidths(), 0);
    });
  }

  ngAfterViewInit(): void {
    const viewport = this.elementRef.nativeElement.querySelector<HTMLElement>(
      '.ui-list-report__viewport',
    );
    if (viewport && typeof ResizeObserver !== 'undefined') {
      let previousWidth = viewport.clientWidth;
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = Math.floor(entries[0]?.contentRect.width ?? 0);
        if (width <= 0 || width === previousWidth) return;
        previousWidth = width;
        this.closeActions();
        this.calculateColumnWidths();
      });
      this.resizeObserver.observe(viewport);
    }
    setTimeout(() => this.calculateColumnWidths(), 0);
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.scrollHandler, {
      capture: true,
    });
    this.resizeObserver?.disconnect();
  }

  protected rowId(row: T, index: number): string {
    const value = (row as Record<string, unknown>)[this.rowKey()];
    return value == null || value === '' ? String(index) : String(value);
  }

  protected isEmphasizedRow(row: T): boolean {
    return this.emphasizeRow()?.(row) ?? false;
  }

  protected onRowClick(row: T, event: Event): void {
    if (!this.clickableRows()) return;
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest('button, a, input, select, textarea')
    ) {
      return;
    }
    this.rowClick.emit(row);
  }

  protected isStickyColumn(index: number): boolean {
    return index < this.normalizedStickyCount();
  }

  protected isStickyEdge(index: number): boolean {
    const count = this.normalizedStickyCount();
    return count > 0 && index === count - 1;
  }

  protected stickyLeft(index: number): number | null {
    if (!this.isStickyColumn(index)) return null;
    const column = this.columns()[index];
    return column ? (this.stickyLeftOffsets()[column.key] ?? 0) : null;
  }

  protected isStickyEnd(index: number): boolean {
    return (
      this.stickyEndColumn() &&
      index === this.columns().length - 1 &&
      !this.isStickyColumn(index)
    );
  }

  protected stickyEnd(index: number): number | null {
    if (!this.isStickyEnd(index)) return null;
    return this.actions().length ? 48 : 0;
  }

  protected treeIndent(level: number): number {
    return level * TREE_INDENT;
  }

  protected toggleExpanded(event: MouseEvent, id: string): void {
    event.stopPropagation();
    const next = new Set(this.expandedIds());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.expandedIds.set(next);
    this.closeActions();
  }

  private rowChildren(row: T): readonly T[] {
    const value = (row as { children?: unknown }).children;
    return Array.isArray(value) ? (value as readonly T[]) : [];
  }

  private branchHasChildren(rows: readonly T[], level: number): boolean {
    if (level >= MAX_TREE_DEPTH - 1) return false;
    return rows.some((row) => this.rowChildren(row).length > 0);
  }

  protected tagColor(column: UiListReportColumn<T>, row: T): UiTagColor {
    const value = this.cellText(column, row);
    return column.tagColor?.(value, row) ?? 'neutral';
  }

  protected isColumnSortable(column: UiListReportColumn<T>): boolean {
    return column.sortable !== false;
  }

  protected ariaSort(
    column: UiListReportColumn<T>,
  ): 'ascending' | 'descending' | 'none' | null {
    if (!this.isColumnSortable(column)) return null;
    const state = this.sortState();
    if (state?.sortBy !== column.key) return 'none';
    return state.order === 'asc' ? 'ascending' : 'descending';
  }

  protected onHeaderClick(column: UiListReportColumn<T>): void {
    if (!this.isColumnSortable(column)) return;
    const previous = this.sortState();
    const next: SortState =
      previous?.sortBy === column.key
        ? {
            sortBy: column.key,
            order: previous.order === 'asc' ? 'desc' : 'asc',
          }
        : { sortBy: column.key, order: 'asc' };
    this.sortState.set(next);
    this.sortChange.emit(next);
  }

  protected cellSwatch(column: UiListReportColumn<T>, row: T): string | null {
    return column.swatch?.(row) || null;
  }

  protected cellText(column: UiListReportColumn<T>, row: T): string {
    const empty = column.emptyText ?? '—';
    if (column.value) return column.value(row) || empty;
    const value = (row as Record<string, unknown>)[column.key];
    return value == null || value === '' ? empty : String(value);
  }

  protected totalCellText(
    column: UiListReportColumn<T>,
    index: number,
  ): string {
    if (index === 0) return this.totalLabel();
    const value = this.totalValues()[column.key];
    return value == null ? '' : String(value);
  }

  protected onColumnFilter(key: string, value: string): void {
    const updated = { ...this.columnFilters(), [key]: value };
    this.columnFilters.set(updated);
    this.columnFiltersChange.emit(updated);
  }

  protected onColumnFilterSelect(
    key: string,
    option: UiSelectOption | null,
  ): void {
    this.onColumnFilter(
      key,
      option ? String(option.value ?? option.label) : '',
    );
  }

  protected onColumnFilterMulti(key: string, options: UiSelectOption[]): void {
    this.onColumnFilter(
      key,
      options
        .map((option) => String(option.value ?? option.label))
        .join(DATA_TABLE_MULTI_FILTER_SEP),
    );
  }

  protected filterValue(key: string): string {
    return this.columnFilters()[key] ?? '';
  }

  protected filterOptionsOf(column: UiListReportColumn<T>): UiSelectOption[] {
    return [...(column.filterOptions ?? [])];
  }

  protected filterSelectedOption(
    column: UiListReportColumn<T>,
  ): UiSelectOption | null {
    const value = this.filterValue(column.key);
    if (!value) return null;
    return (
      this.filterOptionsOf(column).find(
        (option) => String(option.value ?? option.label) === value,
      ) ?? null
    );
  }

  protected filterSelectedMultiOptions(
    column: UiListReportColumn<T>,
  ): UiSelectOption[] {
    const selectedValues = new Set(
      parseColumnMultiFilter(this.filterValue(column.key)),
    );
    return this.filterOptionsOf(column).filter((option) =>
      selectedValues.has(String(option.value ?? option.label)),
    );
  }

  protected closeFilter(key: string): void {
    if (this.openFilterKey() === key) this.openFilterKey.set(null);
  }

  protected visibleActions(row: T): readonly RowAction[] {
    return this.actions()
      .filter((action) => action.visible?.(row) ?? true)
      .map(({ key, label, destructive }) => ({ key, label, destructive }));
  }

  protected openActions(event: MouseEvent, row: T, index: number): void {
    event.stopPropagation();
    if (this.menu()?.rowIndex === index) {
      this.closeActions();
      return;
    }
    const trigger = event.currentTarget as HTMLElement;
    const cell = trigger.closest('td') as HTMLElement;
    const bounds = (cell ?? trigger).getBoundingClientRect();
    const items = this.visibleActions(row);
    if (!items.length) return;
    const itemHeight = 36;
    const menuPadding = 8;
    const estimatedMenuHeight = items.length * itemHeight + menuPadding;
    const openAbove = bounds.top + estimatedMenuHeight > window.innerHeight;
    this.menu.set({
      row,
      rowIndex: index,
      top: openAbove ? bounds.bottom : bounds.top,
      left: bounds.left,
      openAbove,
      items,
    });
  }

  protected closeActions(): void {
    this.menu.set(null);
  }

  protected selectAction(action: RowAction): void {
    const state = this.menu();
    const source = this.actions().find((item) => item.key === action.key);
    if (!state || !source) return;
    this.actionSelect.emit({ action: source, row: state.row as T });
    this.closeActions();
  }

  private calculateColumnWidths(): void {
    const columns: DataGridColumn[] = this.columns().map((column) => ({
      key: column.key,
      header: column.label,
      type: 'string',
      sortable: this.isColumnSortable(column),
      width: column.width,
      minWidth: column.minWidth,
      truncate: column.truncate,
      grow: column.grow,
    }));
    const firstColumn = this.columns()[0];
    const totalRow = this.showTotals()
      ? {
          ...this.totalValues(),
          ...(firstColumn ? { [firstColumn.key]: this.totalLabel() } : {}),
        }
      : null;
    const measurementRows = [
      ...(this.flatRows() as readonly Record<string, unknown>[]),
      ...(totalRow ? [totalRow] : []),
    ];
    const viewport = this.elementRef.nativeElement.querySelector<HTMLElement>(
      '.ui-list-report__viewport',
    );
    const containerWidth = Math.floor(viewport?.clientWidth ?? 0);
    if (containerWidth <= 0) return;
    const result = computeDataTableColumnWidths({
      columns,
      rows: measurementRows,
      containerWidth,
      showActionsColumn: this.actions().length > 0,
      sortingEnabled: true,
      columnSizing: this.columnSizing(),
      getCellText: (row, column) => {
        if (row === totalRow) {
          const value = row[column.key];
          return value == null ? '' : String(value);
        }
        const source = this.columns().find((item) => item.key === column.key);
        return source ? this.cellText(source, row as T) : '';
      },
      options: {
        headerFont: '600 12px "DM Sans", sans-serif',
        dataFont: '400 13px "DM Sans", sans-serif',
      },
    });
    this.colWidths.set(result.colWidths);
    this.tableWidth.set(result.tableWidth);
    this.columnsReady.set(true);
  }

  private flatRows(): readonly T[] {
    const collected: T[] = [];
    const visit = (rows: readonly T[], level: number): void => {
      for (const row of rows) {
        collected.push(row);
        if (level < MAX_TREE_DEPTH - 1) visit(this.rowChildren(row), level + 1);
      }
    };
    visit(this.rows(), 0);
    return collected;
  }

  private sortValue(column: UiListReportColumn<T>, row: T): string | number {
    const raw = (row as Record<string, unknown>)[column.key];
    if (typeof raw === 'number') return raw;
    return this.cellText(column, row);
  }
}

function compareSortValues(
  left: string | number,
  right: string | number,
): number {
  const leftNumber = typeof left === 'number' ? left : parseSortNumber(left);
  const rightNumber =
    typeof right === 'number' ? right : parseSortNumber(right);
  if (leftNumber != null && rightNumber != null)
    return leftNumber - rightNumber;
  return String(left).localeCompare(String(right), 'es', {
    numeric: true,
    sensitivity: 'base',
  });
}

function parseSortNumber(value: string): number | null {
  const normalized = value.trim().replace(/[$\s]/g, '').replace(/,/g, '');
  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return null;
  return Number(normalized);
}
